import seanEnver from '@/assets/images/Sean & Enver.png';
import founders from '@/assets/images/founders.png';
import gdWhiteLogo from '@/assets/images/gd-white-logo.png';
import Footer from '@/components/sections/footer';
import { getFooterPages } from '@/lib/cms';
import { getLocaleFromRequest } from '@/lib/getLocaleFromRequest';
import type { LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await getLocaleFromRequest(request);
  const footerPages = await getFooterPages(locale);

  return json({
    footerPages,
    locale
  });
}

export default function AboutRoute() {
  const { footerPages, locale } = useLoaderData<typeof loader>();
  const { t } = useTranslation();
  return (
    <div className='min-h-screen bg-background'>
      <main className='container mx-auto px-6 py-24 max-w-7xl'>
        {/* Our Story Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className='grid md:grid-cols-2 gap-12 mb-20'
        >
          <div className='space-y-6'>
            <h2 className='text-3xl font-bold text-foreground mb-8'>
              {t('aboutPage.ourStory.title')}
            </h2>
            <div className='space-y-6 text-muted-foreground'>
              <p>{t('aboutPage.ourStory.descriptions.0')}</p>
              <p>{t('aboutPage.ourStory.descriptions.1')}</p>
              <p>{t('aboutPage.ourStory.descriptions.2')}</p>
            </div>
            <div className='mt-8'>
              <img
                src={seanEnver}
                alt='Sean & Enver signature'
                className='h-[35px] mb-2'
              />
              <p className='text-muted-foreground'>
                {t('aboutPage.ourStory.cofoundersTitle')}
              </p>
            </div>
          </div>
          <div className='relative'>
            <img
              src={founders}
              alt='Growdash Founders'
              className='w-full h-full object-cover rounded-lg'
            />
          </div>
        </motion.div>

        {/* Making Data Make Sense Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className='rounded-xl p-16 mb-20'
          style={{
            background: 'linear-gradient(124.91deg, #E5EFFF 0%, #FFFFFF 71.24%)'
          }}
        >
          <h2
            className='text-4xl font-bold mb-12 text-center'
            style={{
              background:
                'linear-gradient(272.32deg, #1FBDB2 31.1%, #0F307E 98.03%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {t('aboutPage.dataSenseSection.title')}
          </h2>
          <div className='grid md:grid-cols-3 gap-8'>
            <div className='bg-white rounded-xl p-8 shadow-sm'>
              <h3 className='text-xl font-semibold mb-4'>
                {t('aboutPage.dataSenseSection.sections.0.title')}
              </h3>
              <p className='text-muted-foreground'>
                {t('aboutPage.dataSenseSection.sections.0.description')}
              </p>
            </div>
            <div className='bg-white rounded-xl p-8 shadow-sm'>
              <h3 className='text-xl font-semibold mb-4'>
                {t('aboutPage.dataSenseSection.sections.1.title')}
              </h3>
              <p className='text-muted-foreground'>
                {t('aboutPage.dataSenseSection.sections.1.description')}
              </p>
            </div>
            <div className='bg-white rounded-xl p-8 shadow-sm'>
              <h3 className='text-xl font-semibold mb-4'>
                {t('aboutPage.dataSenseSection.sections.2.title')}
              </h3>
              <p className='text-muted-foreground'>
                {t('aboutPage.dataSenseSection.sections.2.description')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Growdash in Numbers Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className='rounded-xl p-12'
          style={{
            background:
              'linear-gradient(128.37deg, #1FBDB2 -21.4%, #060081 71.7%)'
          }}
        >
          <div className='flex flex-col items-center mb-12'>
            <div className='inline-flex items-end mb-1'>
              <img src={gdWhiteLogo} alt='Growdash Logo' className='h-8' />
              <h2 className='text-4xl font-bold text-white pl-3'>
                {t('aboutPage.numbersSection.title')}
              </h2>
            </div>
          </div>
          <div className='grid md:grid-cols-2 gap-8'>
            <div className='bg-white bg-opacity-20 p-8 rounded-xl text-white'>
              <div className='flex items-baseline gap-2 mb-2'>
                <span className='text-3xl font-bold'>4</span>
                <span className='text-lg font-semibold'>
                  {t('aboutPage.numbersSection.statistics.0.label')}
                </span>
              </div>
              <p className='text-sm text-left'>
                {t('aboutPage.numbersSection.statistics.0.value')}
              </p>
            </div>

            <div className='bg-white bg-opacity-20 p-8 rounded-xl text-white'>
              <div className='flex items-baseline gap-2 mb-2'>
                <span className='text-3xl font-bold'>350+</span>
                <span className='text-lg font-semibold'>
                  {t('aboutPage.numbersSection.statistics.1.label')}
                </span>
              </div>
              <p className='text-sm text-left'>
                {t('aboutPage.numbersSection.statistics.1.value')}
              </p>
            </div>

            <div className='bg-white bg-opacity-20 p-8 rounded-xl text-white'>
              <div className='flex items-baseline gap-2 mb-2'>
                <span className='text-3xl font-bold'>12M+</span>
                <span className='text-lg font-semibold'>
                  {t('aboutPage.numbersSection.statistics.2.label')}
                </span>
              </div>
              <p className='text-sm text-left'>
                {t('aboutPage.numbersSection.statistics.2.value')}
              </p>
            </div>

            <div className='bg-white bg-opacity-20 p-8 rounded-xl text-white'>
              <div className='flex items-baseline gap-2 mb-2'>
                <span className='text-3xl font-bold'>$235M</span>
                <span className='text-lg font-semibold'>
                  {t('aboutPage.numbersSection.statistics.3.label')}
                </span>
              </div>
              <p className='text-sm text-left'>
                {t('aboutPage.numbersSection.statistics.3.value')}
              </p>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer pages={footerPages} locale={locale} />
    </div>
  );
}
